import { Component, OnDestroy } from '@angular/core';
import { DrawerService } from '../../../../../../core/services/drawer.service';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { finalize, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { DRAWER_IDS } from '../../../../../../core/constants/drawer-ids.constant';
import { LoaderService } from '../../../../../../core/services/loader.service';
import { CommonService } from '../../../../../../shared/services/common.service';
import { FrequencyDrawerParams } from '../../insights-data.interface';
import { InsightsDataService } from '../../insights-data.service';
import { CommonModule } from '@angular/common';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzButtonModule } from 'ng-zorro-antd/button';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { ErrorResponse } from '../../../../../../core/models/error.models';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { AllowOnlyValidNumbersDirective } from '../../../../../../core/direactives/allowedOnlyValidNUmber.directive';
import _ from 'lodash';
@Component({
  selector: 'app-partial-interval',
  standalone: true,
  imports: [
    NzSpinModule,
    NzButtonModule,
    NzInputModule,
    CommonModule,
    NzFormControlComponent,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormModule,
    AllowOnlyValidNumbersDirective,
    NgxMaskDirective,
    ReactiveFormsModule,
  ],
  providers: [provideNgxMask()],
  templateUrl: './partial-interval.component.html',
  styleUrl: './partial-interval.component.scss',
})
export class PartialIntervalComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  drawerParmas: FrequencyDrawerParams = {} as FrequencyDrawerParams;
  form!: FormGroup;
  loading$: Observable<boolean> = of(false);
  constructor(
    private drawerService: DrawerService,
    private fb: FormBuilder,
    private loaderService: LoaderService,
    private insightsService: InsightsDataService,
    private commonService: CommonService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.form = this.fb.group({
      id: [0],
      dataDate: [this.drawerParmas.selectedDate],
      isIntervalFrequency: [true],
      isDuration: [false],
      interval1: [''],
      interval2: [''],
      interval3: [''],
    });
    this.setupDrawerSubscription();
  }
  // Getter function to get unitValue dynamically
  get unitValue(): string {
    return [
      this.form.value.interval1,
      this.form.value.interval2,
      this.form.value.interval3,
    ].join(','); // Join values with comma
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<FrequencyDrawerParams>(DRAWER_IDS.PARTIAL_INTERVAL)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.drawerParmas = params;
          const matchedData = _.find(
            this.drawerParmas.behaviorData.activityDataUnitMultiValues,
            { dataDate: this.drawerParmas.selectedDate }
          );
          if (matchedData) {
            const unitValueArray = matchedData.unitValue
              .split(',')
              .map(val => (val.trim() === '' ? null : Number(val.trim())));
            this.form.patchValue({
              id: matchedData.id,
              interval1: unitValueArray[0],
              interval2: unitValueArray[1],
              interval3: unitValueArray[2],
            });
          }
        }
      });
  }

  get isIntervalFormValid() {
    return (
      this.form.value.interval1 ||
      this.form.value.interval2 ||
      this.form.value.interval3
    );
  }

  submitForm() {
    const payload = {
      unitId: this.drawerParmas.unitId,
      unitMultiValues: [
        {
          id: this.form.value.id,
          dataDate: this.drawerParmas.selectedDate,
          startDataTime: '00:00:01',
          endDataTime: '00:00:01',
          unitValue: this.unitValue,
          isIntervalFrequency: true,
          isDuration: false,
        },
      ],
    };
    this.loaderService.show();
    const operation$ = this.insightsService.saveMultiValue(payload);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            `Frequency interval saved successfully.`
          );
          this.onCancel();
        },
        error: error => {
          this.loaderService.hide();
          this.handleError(error);
        },
      });
  }

  private handleError({ errors, message }: ErrorResponse): void {
    const errorMessages = errors.map(
      err => err.description || err.detailedMessage
    );
    this.commonService.notify('error', errorMessages, message);
  }

  onCancel() {
    this.drawerService.closeDrawer(DRAWER_IDS.PARTIAL_INTERVAL);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  validateInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement; // Explicitly cast event.target
    const value = inputElement.value.trim();

    // Convert to a number
    const num = parseInt(value, 10);

    // If input is not a number or out of range, clear it
    if (isNaN(num) || num < 1 || num > 10) {
      inputElement.value = '';
    }
  }
}
